import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import { Layout } from '@layout/Layout';
import usePlanReportStore, { Period, Scorecard } from '@/store/hr/PlanReportStore';
import usePlanBoardStore from '@/store/hr/PlanBoardStore';
import PeriodSelector from '@components/hr/planReports/PeriodSelector';
import ExportMenu from '@components/hr/planReports/ExportMenu';
import { formatRate } from '@components/hr/planReports/TaskStatusChip';

const KPI_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
];

const KpiCard: React.FC<{ label: string; value: React.ReactNode; sub?: string; gradient: string }> = ({
  label,
  value,
  sub,
  gradient
}) => (
  <Card sx={{ background: gradient, color: '#fff', borderRadius: 2 }}>
    <CardContent>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.9 }}
      >
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const dec = (v: number | null) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));

const PeriodReportPage: React.FC = () => {
  const theme = useTheme();
  const report = usePlanReportStore((s) => s.periodReport);
  const loading = usePlanReportStore((s) => s.loading);
  const error = usePlanReportStore((s) => s.error);
  const fetchPeriodReport = usePlanReportStore((s) => s.fetchPeriodReport);

  // The board endpoint echoes the server's business date; reuse it so the period
  // selector never has to guess "today" on the client.
  const board = usePlanBoardStore((s) => s.board);
  const fetchBoard = usePlanBoardStore((s) => s.fetchBoard);

  const [period, setPeriod] = useState<Period | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (!board) fetchBoard({});
  }, [board, fetchBoard]);

  useEffect(() => {
    if (period) {
      fetchPeriodReport({ from: period.from, to: period.to });
      setPage(0);
    }
  }, [period?.from, period?.to, fetchPeriodReport]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePeriod = useCallback((p: Period) => setPeriod(p), []);

  const summary = report?.summary;
  const rows: Scorecard[] = report?.rows || [];
  const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Period Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Plan compliance and task completion per employee, {period?.label || '…'}
            </Typography>
          </Box>
          <ExportMenu
            report="employees"
            filters={{ from: period?.from, to: period?.to }}
            disabled={!period || rows.length === 0}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <PeriodSelector today={board?.today} onChange={handlePeriod} />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3
          }}
        >
          <KpiCard
            label="Plan compliance"
            value={formatRate(summary?.plan_compliance_rate)}
            sub={`${summary?.planned_days ?? 0} of ${summary?.expected_days ?? 0} working days`}
            gradient={KPI_GRADIENTS[0]}
          />
          <KpiCard
            label="Completion rate"
            value={formatRate(summary?.completion_rate)}
            sub={`${summary?.completed ?? 0} of ${summary?.resolved ?? 0} resolved tasks`}
            gradient={KPI_GRADIENTS[1]}
          />
          <KpiCard
            label="Chronic slips"
            value={summary?.chronic_slip_tasks ?? 0}
            sub="tasks pushed 3+ times"
            gradient={KPI_GRADIENTS[2]}
          />
          <KpiCard
            label="Missed EODs"
            value={summary?.forgot_eod_days ?? 0}
            sub={`${summary?.late_days ?? 0} late starts`}
            gradient={KPI_GRADIENTS[3]}
          />
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Working days they were expected to plan — excludes weekends, holidays, approved leave and opted optional holidays, and is clamped to their hire date.">
                            <span>Expected</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">Planned</TableCell>
                        <TableCell align="center">Plan&nbsp;%</TableCell>
                        <TableCell align="center">No&nbsp;EOD</TableCell>
                        <TableCell align="center">Tasks</TableCell>
                        <TableCell align="center">Done</TableCell>
                        <TableCell align="center">Completion</TableCell>
                        <TableCell align="center">
                          <Tooltip title="The most times any one of their tasks has been pushed to another day.">
                            <span>Worst&nbsp;carry</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">Late</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Average hours, excluding days closed out by a retroactive EOD (those hours are synthesized, not measured).">
                            <span>Avg&nbsp;hrs</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paged.map((r) => (
                        <TableRow key={r.employee_id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box>
                                <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {r.dept_name || r.role_name}
                                </Typography>
                              </Box>
                              {r.has_left && (
                                <Chip label="Left" size="small" variant="outlined" sx={{ height: 20 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{r.expected_days}</TableCell>
                          <TableCell align="center">{r.planned_days}</TableCell>
                          <TableCell align="center">
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                color:
                                  r.plan_compliance_rate !== null && r.plan_compliance_rate < 0.8
                                    ? theme.palette.warning.main
                                    : 'inherit'
                              }}
                            >
                              {formatRate(r.plan_compliance_rate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{r.forgot_eod_days || '—'}</TableCell>
                          <TableCell align="center">{r.tasks_total || '—'}</TableCell>
                          <TableCell align="center">{r.completed || '—'}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            {formatRate(r.completion_rate)}
                          </TableCell>
                          <TableCell align="center">
                            {r.max_carry_count > 0 ? (
                              <Chip
                                label={`${r.max_carry_count}×`}
                                size="small"
                                color={r.max_carry_count >= 3 ? 'warning' : 'default'}
                                sx={{ height: 20, fontWeight: 700 }}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="center">{r.late_days || '—'}</TableCell>
                          <TableCell align="center">{dec(r.avg_hours_excl_retro)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {rows.length === 0 ? (
                  <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography>No plan data in this period.</Typography>
                  </Box>
                ) : (
                  <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={(_e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default PeriodReportPage;
